import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { loadFormStatusDetails } from '../../../store/directusService';
import {
    clearFormContracts,
    clearFormsParticipant,
    loadFormContractByContract,
    loadForms,
    loadFormsParticipantByParticipantId
} from '../../../store/participantService';
import { selectUsersForNames } from '../../../store/userSelectors';
import {
    loadUsersMinimalDetails,
    searchUsersByLoggedInUserServiceIds
} from '../../../store/userService';
import { getObjFromId } from '../../../utils/directusFunctions';
import {
    DEFAULT_TABLE_ROWS_PER_PAGE,
    EDIT_ON_CLOSED_ROLES
} from '../../../utils/formValidation/commonConstants';
import { FORM_STATUS_DETAILS_ERROR } from '../../../utils/formValidation/loadingErrorMessageConstants';
import { stableSort } from '../../../utils/sortFunctions';
import {
    ADVISER,
    hasRole,
    MANAGER,
    QUALITY,
    RECRUITMENT_MANAGER,
    SUPERUSER
} from '../../../utils/userRoles';
import Button from '../../formElements/Button';
import SingleSelect from '../../formElements/SingleSelect';
import ResultsTable from '../../table/ResultsTable';
import LoadingSpinner from '../../ui/LoadingSpinner';

import ConductForm from './ConductForm';
import EditForm from './EditForm';
import FormRow from './FormRow';

import app from '../../../app.module.css';
import classes from './formStyles/form.module.css';

const headCells = [
    { id: 'name', numeric: false, label: 'Title' },
    { id: 'createdByUserId', numeric: true, label: 'Created By' },
    { id: 'authorisedByUserId', numeric: true, label: 'Authorised By' },
    { id: 'lastUpdated', numeric: false, label: 'Last Updated' },
    { id: 'status', numeric: false, label: 'Status' }
];

const initialRowMetaData = {
    order: 'asc',
    orderBy: 'name',
    page: 0,
    rowsPerPage: DEFAULT_TABLE_ROWS_PER_PAGE
};

const acceptedRoles = [ADVISER, MANAGER, QUALITY, RECRUITMENT_MANAGER, SUPERUSER];
const authoriseRoles = [MANAGER, QUALITY, SUPERUSER];

const Form = () => {
    // HOOKS
    const surveyRef = useRef(null);
    const dispatch = useDispatch();

    // LOCAL STATE
    const [rowMetaData, setRowMetaData] = useState(initialRowMetaData);
    const [rows, setRows] = useState([]);
    const [selectedRow, setSelectedRow] = useState({});
    const [showForm, setShowForm] = useState(false);
    const [editForm, setEditForm] = useState(false);
    const [adviser, setAdviser] = useState('');

    const [formMenuItems, setFormMenuItems] = useState([]);
    const [openRowId, setOpenRowId] = useState('');
    const [isDisabled, setIsDisabled] = useState(true);
    const [hasAuthRoles, setHasAuthRoles] = useState(false);
    const [isSuperUser, setIsSuperUser] = useState(false);

    // STORE STATE
    const loggedInUser = useSelector((state) => state.entities.userService.loggedInUser);
    const { roles } = loggedInUser;
    const users = useSelector(selectUsersForNames);
    const formStatusDetails = useSelector(
        (state) => state.entities.directusService.formStatusDetails
    );
    const currentParticipant = useSelector(
        (state) => state.entities.participantService.currentParticipant
    );
    const forms = useSelector((state) => state.entities.participantService.forms);
    const formsParticipant = useSelector(
        (state) => state.entities.participantService.formsParticipant
    );
    const formContracts = useSelector((state) => state.entities.participantService.formContracts);
    const successMessage = useSelector((state) => state.entities.formsState.successMessage);

    // USE EFFECTS
    useEffect(() => {
        !formStatusDetails?.length && dispatch(loadFormStatusDetails());
        dispatch(loadForms());
        dispatch(loadFormContractByContract(currentParticipant.contractId));
        return () => cleanUp();
    }, []);

    useEffect(() => {
        if (!('serviceIds' in loggedInUser)) return;
        if (loggedInUser.serviceIds.length > 0 && users?.length < 1)
            dispatch(searchUsersByLoggedInUserServiceIds(loggedInUser.serviceIds));
    }, [loggedInUser?.serviceIds]);

    useEffect(() => {
        if (showForm) return;
        setRows([]);
        if (currentParticipant?.id) {
            dispatch(loadFormsParticipantByParticipantId(currentParticipant.id));

            const isParticipantClosedForEdit =
                currentParticipant.status === 'Closed' && !hasRole(EDIT_ON_CLOSED_ROLES, roles);
            setIsDisabled(!hasRole(acceptedRoles, roles) || isParticipantClosedForEdit);
            setHasAuthRoles(hasRole(authoriseRoles, roles));
            setIsSuperUser(hasRole([SUPERUSER], roles));
        }
    }, [showForm, currentParticipant, roles]);

    useEffect(() => {
        if (
            formsParticipant?.length < 1 ||
            formStatusDetails?.length < 1 ||
            !users ||
            !('id' in currentParticipant)
        )
            return;
        const formUserIds = [
            ...new Set(
                formsParticipant
                    .map((el) => [el.createdByUserId, el.authorisedByUserId])
                    .flat()
                    .filter((el) => el)
            )
        ];
        const missingUserIds = [...formUserIds, currentParticipant.userId]
            .filter((el) => !users.find((entry) => entry.id === el))
            .filter((el) => el);
        if (missingUserIds.length) dispatch(loadUsersMinimalDetails(missingUserIds));
        else {
            setRows(
                formsParticipant.map((el) => ({
                    ...el,
                    createdEmailAddress: el.createdByUserId
                        ? getObjFromId(users, el.createdByUserId)?.loginEmailAddress || ''
                        : '',
                    authorisedEmailAddress: el.authorisedByUserId
                        ? getObjFromId(users, el.authorisedByUserId)?.loginEmailAddress || ''
                        : ''
                }))
            );
            const participantsAdviser = users.find((el) => el.id === currentParticipant.userId);
            participantsAdviser && setAdviser(participantsAdviser);
        }
    }, [formsParticipant, formStatusDetails, users]);

    useEffect(() => {
        if (formContracts?.length < 1 || forms?.length < 1) return;
        const formsForContracts = formContracts.filter((el) =>
            el.contractIds.some((entry) => entry.includes(currentParticipant.contractId))
        );
        setFormMenuItems(
            forms
                .filter((el) => !el.inactive)
                .filter((el) => formsForContracts.find((entry) => entry.name === el.name))
                .sort((a, b) => a.name.trim().localeCompare(b.name.trim()))
        );
    }, [forms, formContracts]);

    useEffect(() => {
        if (successMessage === 'Form participant has been created successfully') {
            onFormIdChange('');
            dispatch(loadFormsParticipantByParticipantId(currentParticipant.id));
        }
    }, [successMessage]);

    // HELPER FNS
    const loadingError = () => {
        if (formStatusDetails?.length < 1) return FORM_STATUS_DETAILS_ERROR;
    };

    const createRows = () =>
        stableSort(rows, rowMetaData.orderBy, rowMetaData.order).map((el) => (
            <FormRow
                key={el.id}
                row={el}
                participantsAdviser={adviser}
                hasAuthRoles={hasAuthRoles}
                isSuperUser={isSuperUser}
                isDisabled={isDisabled}
                toggleDropdown={setOpenRowId}
                openId={openRowId}
                showForm={showForm}
                editForm={editForm}
                onEdit={onEdit}
            />
        ));

    const cleanUp = () => {
        dispatch(clearFormsParticipant());
        dispatch(clearFormContracts());
    };

    // EVENT HANDLERS
    const onFormIdChange = (chosenId) => {
        setSelectedRow({
            id: null,
            formId: chosenId,
            participantId: '',
            name: '',
            status: '',
            statusId: '',
            dateStarted: '',
            dateCompleted: '',
            data: {},
            history: [],
            authorisedEmailAddress: '',
            createdByUserId: '',
            createdEmailAddress: '',
            lastUpdated: ''
        });
    };

    // EVENT HANDLER CALLBACKS
    const onEdit = (e, row) => {
        e.stopPropagation();
        setSelectedRow(row);
        setEditForm(true);

        surveyRef.current.scrollIntoView();
    };

    // AWAITING CONTENT
    const errorMsg = loadingError();
    if (errorMsg) return <LoadingSpinner content={errorMsg} />;

    // RENDER
    return (
        <div className={classes.forms}>
            <div className={classes.tableSection}>
                {rows.length < 1 ? (
                    <div>No forms found</div>
                ) : (
                    <ResultsTable
                        defaultOrderBy={rowMetaData.orderBy}
                        sortOrder={rowMetaData.order}
                        headCells={headCells}
                        passRowMetaDataUp={setRowMetaData}
                        tableRows={createRows()}
                        totalResults={rows.length}
                    />
                )}
            </div>
            <div>
                {!editForm && !showForm && (
                    <div className={app.fadein}>
                        <SingleSelect
                            id={'formId'}
                            label={'Please select a form from the list above to start it'}
                            placeholder="Please select a form..."
                            disabled={isDisabled}
                            mandatory={true}
                            menuItems={formMenuItems}
                            selectedId={selectedRow.formId || ''}
                            selected={
                                formMenuItems.find((el) => el.id === selectedRow.formId) || {}
                            }
                            onChange={(chosenId) => onFormIdChange(chosenId)}
                        />
                        <Button
                            id="createFormButton"
                            disabled={isDisabled || !selectedRow.formId || showForm}
                            type="button"
                            content="Create Form"
                            icon={true}
                            onClick={() => setShowForm(true)}
                        />
                    </div>
                )}
                <div ref={surveyRef}>
                    {showForm && (
                        <div className={app.fadein}>
                            <ConductForm
                                formId={selectedRow.formId}
                                adviser={adviser}
                                onClose={() => setShowForm(false)}
                            />
                        </div>
                    )}
                    {editForm && (
                        <div className={app.fadein}>
                            <EditForm
                                row={selectedRow}
                                adviser={adviser}
                                onClose={() => setEditForm(false)}
                                roles={roles}
                            />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Form;
